import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { businessInfo } from '../data/mock';

export const ContactSection = ({ onContactClick }) => {
  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-emerald-900 mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Ready to protect your brand? Contact us today for a free consultation with our trademark experts.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-emerald-900 mb-6">
                Contact Information
              </h3>
              <div className="space-y-6">
                <Card className="border-emerald-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 flex items-start">
                    <div className="bg-emerald-100 p-3 rounded-full mr-4">
                      <MapPin className="h-6 w-6 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-emerald-900 mb-1">Office Address</h4>
                      <p className="text-gray-600">{businessInfo.address}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-emerald-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 flex items-start">
                    <div className="bg-amber-100 p-3 rounded-full mr-4">
                      <Phone className="h-6 w-6 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-emerald-900 mb-1">Phone Number</h4>
                      <a href={`tel:${businessInfo.phone}`} className="text-gray-600 hover:text-emerald-700">
                        {businessInfo.phone}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-emerald-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 flex items-start">
                    <div className="bg-emerald-100 p-3 rounded-full mr-4">
                      <Mail className="h-6 w-6 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-emerald-900 mb-1">Email Address</h4>
                      <a href={`mailto:${businessInfo.email}`} className="text-gray-600 hover:text-emerald-700">
                        {businessInfo.email}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-emerald-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 flex items-start">
                    <div className="bg-amber-100 p-3 rounded-full mr-4">
                      <Clock className="h-6 w-6 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-emerald-900 mb-1">Business Hours</h4>
                      <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p className="text-gray-600">Saturday: 10:00 AM - 4:00 PM</p>
                      <p className="text-gray-600">Sunday: Closed</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Image */}
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d"
                alt="Contact our trademark registration experts"
                className="rounded-2xl shadow-xl w-full h-64 object-cover"
              />
            </div>
          </div>

          {/* Quick Contact CTA */}
          <div>
            <Card className="border-2 border-emerald-200 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-emerald-900 mb-4">
                  Schedule Your Free Consultation
                </h3>
                <p className="text-gray-600 mb-6">
                  Click below to open our contact form and get started with your trademark registration today. Our experts will respond within 24 hours.
                </p>

                <Button
                  onClick={onContactClick}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-6 text-lg mb-6 transition-all duration-300"
                >
                  Open Contact Form
                </Button>

                <div className="bg-gradient-to-br from-emerald-50 to-amber-50 rounded-xl p-6">
                  <h4 className="font-bold text-emerald-900 mb-3">What to Expect:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-amber-600 font-bold mr-2">1.</span>
                      <span>Submit your information through our secure form</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-600 font-bold mr-2">2.</span>
                      <span>Receive a response within 24 hours</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-600 font-bold mr-2">3.</span>
                      <span>Schedule a free consultation call</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-600 font-bold mr-2">4.</span>
                      <span>Get started with trademark protection</span>
                    </li>
                  </ul>
                </div>

                <div className="mt-6 pt-6 border-t border-emerald-200">
                  <p className="text-center text-sm text-gray-600">
                    Or call us directly at{' '}
                    <a href={`tel:${businessInfo.phone}`} className="text-emerald-700 font-semibold hover:text-emerald-800">
                      {businessInfo.phone}
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
